import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { GoogleButton } from '../ui/GoogleButton';
import { FacebookButton } from '../ui/FacebookButton';

interface SocialLoginSectionProps {
  onGoogleSignIn: () => void;
  onFacebookSignIn: () => void;
  isGoogleLoading: boolean;
  isFacebookLoading: boolean;
  disabled?: boolean;
}

export const SocialLoginSection: React.FC<SocialLoginSectionProps> = ({
  onGoogleSignIn,
  onFacebookSignIn,
  isGoogleLoading,
  isFacebookLoading,
  disabled = false
}) => {
  const { t } = useTranslation();
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.3 }}
      className="space-y-6"
    >
      {/* Divider with enhanced styling */}
      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <motion.div 
            className="w-full border-t border-white/10"
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 0.8, delay: 0.5 }}
          />
        </div>
        <div className="relative flex justify-center text-xs uppercase tracking-wider">
          <motion.span 
            className="px-4 text-white/40 font-semibold"
            style={{ background: 'transparent' }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7 }}
          >
            {t('auth.social.divider', 'Or continue with')}
          </motion.span>
        </div>
      </div>

      {/* Social Login Buttons */}
      <div className="space-y-4">
        <GoogleButton
          onClick={onGoogleSignIn}
          loading={isGoogleLoading}
          disabled={disabled || isGoogleLoading || isFacebookLoading}
          className="shadow-lg hover:shadow-xl transition-shadow duration-300"
        >
          {t('auth.social.google', 'Continue with Google')}
        </GoogleButton>

        <FacebookButton
          onClick={onFacebookSignIn}
          loading={isFacebookLoading}
          disabled={disabled || isGoogleLoading || isFacebookLoading}
          className="shadow-lg hover:shadow-xl transition-shadow duration-300"
        >
          {t('auth.social.facebook', 'Continue with Facebook')}
        </FacebookButton>
      </div>

      {/* Security notice */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2 }}
        className="rounded-2xl p-4"
        style={{ background: 'rgba(59, 130, 246, 0.08)', border: '1px solid rgba(59, 130, 246, 0.2)' }}
      >
        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0">
            <svg className="w-5 h-5 text-blue-400 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
            </svg>
          </div>
          <div>
            <h4 className="text-sm font-semibold text-blue-200">{t('auth.social.secureTitle', 'Secure & Private')}</h4>
            <p className="text-xs text-blue-300/80 mt-1">
              {t('auth.social.secureDesc', 'Your data is protected with enterprise-grade security. We never share your personal information.')}
            </p>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};
